import {
  ComponentPropsWithoutRef,
  useCallback,
  useState,
  useEffect,
} from "react";
import {
  Dropdown,
  DropdownButton,
  Modal,
  OverlayTrigger,
  Tooltip,
  InputGroup,
  FormControl,
  Button,
} from "react-bootstrap";
import messages from "messages";
// components
import ModalLayout from "components/modal/modal-layout/ModalLayout";
import Checkbox from "components/checkbox/Checkbox";
import { ReactComponent as ChevronDown } from "assets/svg/outline-chevron-down-16.svg";
import { ReactComponent as OutlineInfoCircle } from "assets/svg/outline-info-circle.svg";
// hooks
import { useOrchidPro } from "components/Dashboard/DoctorDashboard/contexts/orchid-pro-context";
// utils
import getClasses from "utils/getClasses";
// styles
import styles from "./styles.module.scss";
// services
import postOrchidPro from "services/postOrchidPro";
import postOrchidProCompleteSchedulerTutorial from "services/postOrchidProCompleteSchedulerTutorial";
import { AmplitudePageName, track } from "utils/amplitude";

const ANALYTICS_COMPONENT_NAME = "cancellation-policy-modal";

function getHourOption(hours: number) {
  return `Less than ${hours} hours`;
}
const HOUR_OPTIONS = [24, 48, 72, 96] as const;

// https://www.figma.com/file/YQmVxcqfIiUzotzmuNzezZ/Orchid_Doctors_Desktop?node-id=1014%3A11451
const FEE_TYPE_OPTIONS = Object.freeze({
  FLAT: { id: "FLAT", stringKey: "flatFee" },
  PERCENT: { id: "PERCENT", stringKey: "sessionRatePercentage" },
} as const);

/**
 * default average cancellation for orchid pro
 */
const DEFAULT_POLICY = Object.freeze({
  FEE_TYPE: FEE_TYPE_OPTIONS.PERCENT.id,
  VALUE: 20,
  HOURS: 24,
});

type Props = ComponentPropsWithoutRef<typeof Modal> & {
  // hit the close button
  onHide: () => void;
  // hit the apply (should call onHide inside component)
  onApply: () => void;
  // hit the cancel (should call onHide inside component)
  onCancel: () => void;
  analyticsPageName: AmplitudePageName;
};

const CancellationPolicyManager = ({
  show,
  onHide,
  onCancel,
  onApply,
  analyticsPageName,
}: Props) => {
  const { orchidPro, setOrchidPro } = useOrchidPro();
  const [checked, setChecked] = useState(false);

  const [hours, setHours] = useState<number | undefined>(undefined);
  const [feeType, setFeeType] = useState<keyof typeof FEE_TYPE_OPTIONS>(
    FEE_TYPE_OPTIONS.FLAT.id,
  );
  const [value, setValue] = useState<number | null>(null);

  // if set to true, keep other fields value
  useEffect(() => {
    if (!checked && !value && !hours) {
      setFeeType(DEFAULT_POLICY.FEE_TYPE);
      setHours(DEFAULT_POLICY.HOURS);
      setValue(DEFAULT_POLICY.VALUE);
    }
  }, [checked, hours, value]);

  const handleApplyDefault = () => {
    setChecked(false);
    setHours(DEFAULT_POLICY.HOURS);
    setFeeType(DEFAULT_POLICY.FEE_TYPE);
    setValue(DEFAULT_POLICY.VALUE);
  };

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<any>();

  const syncData = useCallback(() => {
    const {
      cancellation_notice_hours,
      cancellation_policy_flat_fee,
      cancellation_policy_percent_fee,
    } = orchidPro || {};
    const nextChecked =
      (cancellation_notice_hours === 0 &&
        cancellation_policy_flat_fee === null &&
        cancellation_policy_percent_fee === null) ||
      (cancellation_notice_hours === null &&
        cancellation_policy_flat_fee === null &&
        cancellation_policy_percent_fee === null);
    setChecked(nextChecked);
    setHours(cancellation_notice_hours ?? undefined);
    setFeeType(
      cancellation_policy_percent_fee !== undefined &&
        cancellation_policy_percent_fee !== null
        ? FEE_TYPE_OPTIONS.PERCENT.id
        : FEE_TYPE_OPTIONS.FLAT.id,
    );
    setValue(
      (cancellation_policy_flat_fee || cancellation_policy_percent_fee) ?? null,
    );
  }, [orchidPro]);

  // sync data from api
  useEffect(() => {
    syncData();
  }, [syncData]);

  // when changed, clear error
  useEffect(() => {
    setError(undefined);
  }, [checked, hours, feeType, value]);

  const reset = () => {
    setError(undefined);
    setBusy(false);
  };

  const handleHide = () => {
    reset();
    onHide();
  };

  const handleCancel = () => {
    reset();
    syncData();
    onCancel();
  };

  const handleApply = async () => {
    setError(undefined);
    setBusy(true);
    const cancellationPolicy = checked
      ? {
          clear_cancellation_policy: true,
          cancellation_notice_hours: null,
          cancellation_policy_flat_fee: null,
          cancellation_policy_percent_fee: null,
        }
      : {
          clear_cancellation_policy: false,
          cancellation_notice_hours: hours,
          ...(feeType === FEE_TYPE_OPTIONS.FLAT.id
            ? {
                cancellation_policy_flat_fee: value,
                cancellation_policy_percent_fee: null,
              }
            : {
                cancellation_policy_flat_fee: null,
                cancellation_policy_percent_fee: value,
              }),
        };
    try {
      const nextOrchidPro = await postOrchidPro({
        ...cancellationPolicy,
      });
      setOrchidPro(nextOrchidPro);
      // call the complete scheduler tutorial
      await postOrchidProCompleteSchedulerTutorial();
      reset();
      if (onApply) {
        onApply();
      }
    } catch (error) {
      setError(error);
    } finally {
      setBusy(false);
    }
  };

  const isFinished =
    checked ||
    (hours !== null &&
      hours !== undefined &&
      value !== null &&
      value !== undefined);

  const disabled = busy || !isFinished;

  return (
    <ModalLayout
      show={show}
      centered
      onHide={() => {
        track(
          analyticsPageName,
          [ANALYTICS_COMPONENT_NAME, "close-modal-button"],
          "click",
        );

        handleHide();
      }}
      error={error}
      Header={messages.setCancellationPolicy}
      Footer={
        <>
          <Button
            variant={"outline-primary"}
            className={styles["cancellation-policy-manager__action"]}
            onClick={() => {
              track(
                analyticsPageName,
                [ANALYTICS_COMPONENT_NAME, "cancel-button"],
                "click",
              );
              handleCancel();
            }}
            disabled={busy}
          >
            {messages.cancel}
          </Button>
          <Button
            className={styles["cancellation-policy-manager__action"]}
            disabled={disabled}
            onClick={() => {
              track(
                analyticsPageName,
                [ANALYTICS_COMPONENT_NAME, "save-button"],
                "click",
              );
              handleApply();
            }}
          >
            {messages.save}
          </Button>
        </>
      }
      disabled={busy}
      className={styles["cancellation-policy-manager"]}
      headerClass={styles["cancellation-policy-manager__header"]}
      bodyClass={styles["cancellation-policy-manager__body"]}
      footerClass={styles["cancellation-policy-manager__footer"]}
    >
      <div className={styles["cancellation-policy-manager__sub-title"]}>
        {messages.sometimesYourPatients}
      </div>
      <div className={styles["cancellation-policy-manager__options"]}>
        <div
          className={styles["cancellation-policy-manager__cancellation-notice"]}
        >
          <label
            htmlFor="fee-type-option"
            className={styles["cancellation-policy-manager__label"]}
          >
            <span className="mr-1">{messages.cancellationNotice}</span>
            <OverlayTrigger
              placement={"top"}
              overlay={
                <Tooltip
                  id="my-cancellation-notice-tooltip"
                  className={styles["cancellation-policy-manager__tooltip"]}
                >
                  {messages.ifAPatientCancelsWithin}
                </Tooltip>
              }
            >
              <OutlineInfoCircle
                className={styles["cancellation-policy-manager__icon"]}
              />
            </OverlayTrigger>
          </label>

          <DropdownButton
            disabled={checked}
            id="fee-type-option"
            className={getClasses(
              styles["cancellation-policy-manager__dropdown-button"],
              styles["cancellation-policy-manager__dropdown-button--hour"],
              "w-100",
            )}
            variant="outline-secondary"
            style={{
              color: !hours ? "var(--light-gray)" : "inherit",
            }}
            title={
              <span
                className={getClasses(
                  styles[
                    "cancellation-policy-manager__dropdown-button-label-container"
                  ],
                  checked &&
                    styles[
                      "cancellation-policy-manager__dropdown-button-label-container--disabled"
                    ],
                )}
              >
                <span
                  className={
                    styles["cancellation-policy-manager__dropdown-button-label"]
                  }
                >
                  {!hours ? messages.selectAnOption : getHourOption(hours)}
                </span>

                <ChevronDown className="ml-auto" />
              </span>
            }
          >
            {HOUR_OPTIONS.map((el) => (
              <Dropdown.Item
                className="rg-14"
                key={el}
                onClick={() => {
                  track(
                    analyticsPageName,
                    [
                      ANALYTICS_COMPONENT_NAME,
                      "cancellation-notice-hours-option",
                    ],
                    "click",
                    { eventProperties: { hours: el } },
                  );
                  setHours(el);
                }}
              >
                {getHourOption(el)}
              </Dropdown.Item>
            ))}
          </DropdownButton>
        </div>

        <div
          className={styles["cancellation-policy-manager__cancellation-fee"]}
        >
          <label
            htmlFor="fee-option"
            className={styles["cancellation-policy-manager__label"]}
          >
            {messages.cancellationFee}
          </label>
          <InputGroup className="w-100 rg-14" id="fee-option">
            <DropdownButton
              disabled={checked}
              className={getClasses(
                styles["cancellation-policy-manager__dropdown-button"],
                styles["cancellation-policy-manager__dropdown-button--type"],
              )}
              variant="outline-secondary"
              title={
                <span
                  className={getClasses(
                    styles[
                      "cancellation-policy-manager__dropdown-button-label-container"
                    ],
                    checked &&
                      styles[
                        "cancellation-policy-manager__dropdown-button-label-container--disabled"
                      ],
                  )}
                >
                  <span className="mr-auto rg-14">
                    {messages[FEE_TYPE_OPTIONS[feeType].stringKey]}
                  </span>
                  <ChevronDown className="ml-auto" />
                </span>
              }
              id="input-group-dropdown-1"
            >
              <Dropdown.Item
                className="rg-14"
                onClick={() => {
                  track(
                    analyticsPageName,
                    [ANALYTICS_COMPONENT_NAME, "flat-fee-option"],
                    "click",
                  );
                  setFeeType(FEE_TYPE_OPTIONS.FLAT.id);
                  setValue(null);
                }}
              >
                {messages.flatFee}
              </Dropdown.Item>
              <Dropdown.Item
                className="rg-14"
                onClick={() => {
                  track(
                    analyticsPageName,
                    [ANALYTICS_COMPONENT_NAME, "%-of-session-rate-option"],
                    "click",
                  );
                  setFeeType(FEE_TYPE_OPTIONS.PERCENT.id);
                  setValue(null);
                }}
              >
                {messages.sessionRatePercentage}
              </Dropdown.Item>
            </DropdownButton>
            {feeType === FEE_TYPE_OPTIONS.FLAT.id && (
              <InputGroup.Text
                className={getClasses(
                  styles["cancellation-policy-manager__addon"],
                  styles["cancellation-policy-manager__addon-text"],
                  styles["cancellation-policy-manager__addon-text--prepend"],
                  checked &&
                    styles[
                      "cancellation-policy-manager__addon-text--disabled"
                    ],
                )}
                id="fee-type-unit"
              >
                {"$"}
              </InputGroup.Text>
            )}
            <FormControl
              disabled={checked}
              className={getClasses(
                styles["cancellation-policy-manager__input"],
                "rg-14",
              )}
              aria-describedby="fee-type-unit"
              type="number"
              min={0}
              // https://orchid-dev.my.to/api/users/docs#/orchid_pro/update_current_orchid_pro_api_users_v1_orchid_pro__put
              max={feeType === FEE_TYPE_OPTIONS.FLAT.id ? 10000 : 100}
              value={value ?? ""}
              onKeyDown={(e) => {
                if (["-", "+"].includes(e.key)) {
                  e.preventDefault();
                  e.stopPropagation();
                  return false;
                }
              }}
              onChange={(e) => {
                if (e.target.value === "") {
                  return setValue(null);
                }
                setValue(+e.target.value);
              }}
              placeholder={"10.00"}
            />
            {feeType === FEE_TYPE_OPTIONS.PERCENT.id && (
              <InputGroup.Text
                className={getClasses(
                  styles["cancellation-policy-manager__addon-text"],
                  styles["cancellation-policy-manager__addon-text--append"],
                  checked &&
                    styles[
                      "cancellation-policy-manager__addon-text--disabled"
                    ],
                )}
                id="fee-type-unit"
              >
                {"%"}
              </InputGroup.Text>
            )}
          </InputGroup>
        </div>

        {/* apply default button */}
        <Button
          disabled={checked}
          variant={"link"}
          className={styles["cancellation-policy-manager__default-btn"]}
          onClick={() => {
            track(
              analyticsPageName,
              [ANALYTICS_COMPONENT_NAME, "set-to-default-button"],
              "click",
            );
            handleApplyDefault();
          }}
        >
          {messages.setToDefault}
        </Button>
      </div>
      <Checkbox
        id="add-policy"
        label={messages.noCancellationPolicy}
        checked={checked}
        onChange={(e) => {
          track(
            analyticsPageName,
            [ANALYTICS_COMPONENT_NAME, "no-cancellation-policy-toggle"],
            "click",
          );
          setChecked(e.target.checked);
        }}
      />
    </ModalLayout>
  );
};
export default CancellationPolicyManager;
